import { useRef, useState, useEffect } from "react";
import { Link } from "wouter";
import { ArrowRight } from "lucide-react";
import { BorderBeam } from "@/components/ui/border-beam";
import { BlurFade } from "@/components/ui/blur-fade";

/* ── Sherpa Homepage Symbol ──────────────────────────────────── */

export interface SherpaHomeProps {
  className?: string;
}

export function SherpaHome({ className }: SherpaHomeProps) {
  const imgRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = imgRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section
      className={`relative bg-charcoal overflow-x-clip overflow-y-visible ${className ?? ""}`}
      data-testid="section-sherpa-home"
    >
      {/* Dot matrix overlay */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage:
            "radial-gradient(circle, hsla(48, 20%, 92%, 0.06) 1px, transparent 1px)",
          backgroundSize: "16px 16px",
        }}
        aria-hidden="true"
      />

      <div className="hidden lg:flex relative z-10" style={{ minHeight: "80vh" }}>
        {/* Left: copy — sticky, vertically centered */}
        <div
          style={{
            width: "40%",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            paddingTop: "var(--space-section-top)",
            paddingBottom: "var(--space-section-bottom)",
          }}
        >
          <div
            style={{
              marginLeft: "max(24px, calc((100vw - 1280px) / 2))",
              maxWidth: 480,
              paddingRight: 40,
            }}
          >
            <BlurFade inView delay={0.1}>
              <span className="type-eyebrow" style={{ color: "var(--terra-lt)" }}>
                PROPRIETARY TECHNOLOGY
              </span>
            </BlurFade>

            <BlurFade inView delay={0.15}>
              <h2
                className="type-h1 italic"
                style={{ color: "var(--cream)", marginTop: "var(--space-gap-sm)" }}
              >
                SHERPA<sup style={{ fontSize: "0.4em", verticalAlign: "super", opacity: 0.6, fontStyle: "normal" }}>OS</sup>
              </h2>
            </BlurFade>

            <BlurFade inView delay={0.2}>
              <p
                className="type-body"
                style={{
                  color: "var(--text-muted-on-dark)",
                  marginTop: "var(--space-gap-sm)",
                }}
              >
                We invested seven figures building SHERPA<sup style={{ fontSize: "0.7em" }}>OS</sup> in-house.
                It pulls every data source into one command center... Google, Meta,
                Shopify, your CRM, call tracking, all of it. Then it finds the
                patterns, flags the anomalies, and surfaces the moves that a human
                team would take three days to find.
              </p>
            </BlurFade>

            <BlurFade inView delay={0.25}>
              {/* Interim: /sherpa is under construction — route interest to
                  /contact (same destination sherpa-section uses) until the
                  platform page is built. */}
              <Link href="/contact">
                <span
                  className="type-nav inline-flex items-center cursor-pointer group"
                  style={{
                    color: "var(--cream)",
                    gap: 8,
                    marginTop: "var(--space-gap-md)",
                    borderBottom: "1px solid var(--cream-30)",
                    paddingBottom: 4,
                  }}
                >
                  TALK TO US ABOUT SHERPA
                  <ArrowRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1"
                    style={{ transitionDuration: "var(--t-quick)" }}
                  />
                </span>
              </Link>
            </BlurFade>
          </div>
        </div>

        {/* Right: screenshot — overflows right edge, slides in */}
        <div
          ref={imgRef}
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            paddingLeft: "clamp(16px, 2vw, 32px)",
            paddingTop: "var(--space-section-top)",
            paddingBottom: "var(--space-section-bottom)",
          }}
        >
          <div
            style={{
              position: "relative",
              width: "clamp(700px, 62vw, 1100px)",
              flexShrink: 0,
              overflow: "hidden",
              borderRadius: 10,
              opacity: visible ? 1 : 0,
              transform: visible ? "translateX(0)" : "translateX(80px)",
              transition: "opacity 1s cubic-bezier(0.16, 1, 0.3, 1), transform 1s cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          >
            <img
              src="/images/sherpa/change-drivers.webp"
              alt="SHERPA OS dashboard showing change driver analysis"
              className="w-full"
              style={{
                display: "block",
                border: "0.5px solid var(--cream-30)",
                borderRadius: 10,
              }}
              width={1200}
              height={750}
              loading="lazy"
            />
            <BorderBeam
              size={200}
              duration={10}
              colorFrom="hsl(246, 42%, 62%)"
              colorTo="#E8643A"
              borderWidth={2}
            />
          </div>
        </div>
      </div>

      {/* Mobile layout */}
      <div className="lg:hidden relative z-10 container-content section-padding-y">
        <BlurFade inView delay={0.1}>
          <span className="type-eyebrow" style={{ color: "var(--terra-lt)" }}>
            PROPRIETARY TECHNOLOGY
          </span>
        </BlurFade>
        <BlurFade inView delay={0.15}>
          <h2
            className="type-h1 italic"
            style={{ color: "var(--cream)", marginTop: "var(--space-gap-sm)" }}
          >
            SHERPA<sup style={{ fontSize: "0.4em", verticalAlign: "super", opacity: 0.6, fontStyle: "normal" }}>OS</sup>
          </h2>
        </BlurFade>
        <BlurFade inView delay={0.2}>
          <p className="type-body" style={{ color: "var(--text-muted-on-dark)", marginTop: "var(--space-gap-sm)" }}>
            We invested seven figures building SHERPA<sup style={{ fontSize: "0.7em" }}>OS</sup> in-house.
            It pulls every data source into one command center... Google, Meta,
            Shopify, your CRM, call tracking, all of it.
          </p>
        </BlurFade>
        <BlurFade inView delay={0.25}>
          <div style={{ position: "relative", marginTop: "var(--space-gap-md)", borderRadius: 4, overflow: "hidden" }}>
            <img
              src="/images/sherpa/change-drivers.webp"
              alt="SHERPA OS dashboard"
              className="w-full"
              style={{ display: "block", border: "0.5px solid var(--cream-30)", borderRadius: 4 }}
              width={1200}
              height={750}
              loading="lazy"
            />
            <BorderBeam size={150} duration={10} colorFrom="hsl(246, 42%, 62%)" colorTo="#E8643A" borderWidth={2} />
          </div>
        </BlurFade>
        <BlurFade inView delay={0.3}>
          {/* Interim: /sherpa is under construction — see comment above. */}
          <Link href="/contact">
            <span className="type-nav inline-flex items-center cursor-pointer group" style={{ color: "var(--cream)", gap: 8, marginTop: "var(--space-gap-md)", borderBottom: "1px solid var(--cream-30)", paddingBottom: 4 }}>
              TALK TO US ABOUT SHERPA
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" style={{ transitionDuration: "var(--t-quick)" }} />
            </span>
          </Link>
        </BlurFade>
      </div>
    </section>
  );
}
